import _ from "lodash";
// External
import React from "react";
import classNames from "classnames";
import { Button, IconButton } from "@toddle-edu/ds-web";
import { AddOutlined, MinusOutlined } from "@toddle-edu/ds-icons";

// Internal
import { WORKSHEET_AI_QUESTION_OPTIONS } from "Worksheet/modules/WorksheetConstants";

// Relative
import classes from "./AiQuestionGeneratorQuestionDetails.scss";
import {
  CONTEXT_FIELDS,
  MAX_QUESTION_GENERATION_COUNT,
} from "../AiQuestionGeneratorConstants";

const { CF_TYPE_OF_QUESTIONS, CF_NUMBER_OF_QUESTIONS } = CONTEXT_FIELDS;
const QUESTION_TYPE_OPTIONS = [
  { key: "ALL", label: "common:all" },
  ...WORKSHEET_AI_QUESTION_OPTIONS,
];

const SelectionButton = ({ itemKey, children, isSelected, onClick }) => {
  return (
    <Button
      dsVersion="2.0"
      type="outlined"
      size="small"
      color="neutral"
      onClick={onClick}
      className={classNames("text-body-s", {
        [classes.questionTypeItem]: true,
        [classes.questionTypeItemActive]: isSelected,
        [classes.questionTypeItemAuto]: itemKey === "AUTO" && isSelected,
      })}
      testId={`worksheet-create-with-ai-question-type-${itemKey}`}
    >
      {children}
    </Button>
  );
};

/**
 *
 * @param {*} param0
 * @returns Question Count and Question Types selector
 */
const AiQuestionGeneratorQuestionDetails = props => {
  const { t, questionTypes, questionCount, onChange } = props;
  const isAutomaticSelection = questionCount === 0;

  /**
   *
   * @param {*} value
   * @returns Updating Question Count
   */
  const updateQuestionCount = value => {
    if (_.isNaN(value)) return;
    // * Ensure value is between 1 and 10
    const numValue = parseInt(value) || 1;
    const boundedValue = Math.min(
      Math.max(numValue, 1),
      MAX_QUESTION_GENERATION_COUNT
    );
    onChange(CF_NUMBER_OF_QUESTIONS, boundedValue);
  };

  /**
   *
   * @param {*} typeKey
   * @returns Updating Question Types
   */
  const updateQuestionTypes = typeKey => {
    let updatedTypes = [];
    if (typeKey === "AUTO") {
      updatedTypes = ["AUTO"];
    } else {
      if (typeKey === "ALL") {
        updatedTypes = WORKSHEET_AI_QUESTION_OPTIONS.map(option => option.key);
      } else if (_.includes(questionTypes, typeKey)) {
        updatedTypes = _.filter(questionTypes, qt => qt !== typeKey);
      } else {
        updatedTypes = [...questionTypes, typeKey];
      }
      updatedTypes = _.filter(updatedTypes, qt => qt !== "AUTO");
      if (_.isEmpty(updatedTypes)) {
        updatedTypes = ["AUTO"];
      }
    }

    onChange(CF_TYPE_OF_QUESTIONS, updatedTypes);
  };

  // * Is Question Type Selected
  const isTypeSelected = typeKey => {
    return typeKey === "ALL"
      ? _.size(questionTypes) === _.size(WORKSHEET_AI_QUESTION_OPTIONS)
      : _.includes(questionTypes, typeKey);
  };

  return (
    <div className={classes.contentWrapper}>
      <div className={classes.questionDetailsItem}>
        <div className="text-label">{t("worksheet:question_type")}</div>
        <div className={classes.questionTypeContainer}>
          <SelectionButton
            itemKey={"AUTO"}
            isSelected={isTypeSelected("AUTO")}
            onClick={() => updateQuestionTypes("AUTO")}
          >
            {t("worksheet:auto")}
          </SelectionButton>
          <div className={classes.separator} />
          {_.map(QUESTION_TYPE_OPTIONS, type => {
            return (
              <SelectionButton
                key={type.key}
                itemKey={type.key}
                isSelected={isTypeSelected(type.key)}
                onClick={() => updateQuestionTypes(type.key)}
              >
                {t(type.label)}
              </SelectionButton>
            );
          })}
        </div>
      </div>
      <div className={classes.questionDetailsItem}>
        <div className="text-label">{t("worksheet:number_of_questions")}</div>
        <div className={classes.questionTypeContainer}>
          <SelectionButton
            itemKey={"AUTO"}
            isSelected={isAutomaticSelection}
            onClick={() => onChange(CF_NUMBER_OF_QUESTIONS, 0)}
          >
            {t("worksheet:auto")}
          </SelectionButton>
          <div className={classes.separator} />
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            size="small"
            icon={<MinusOutlined />}
            onClick={() => updateQuestionCount(questionCount - 1)}
            disabled={questionCount <= 1}
            testId="worksheet-create-with-ai-question-count-decrement"
          />
          <input
            min={1}
            max={MAX_QUESTION_GENERATION_COUNT}
            value={isAutomaticSelection ? "-" : questionCount}
            className={classNames("text-body", classes.questionCountInput)}
            onChange={e => updateQuestionCount(e.target.value)}
            pattern="[0-9]*"
            aria-live="polite"
            aria-label={t("worksheet:number_of_questions")}
            disabled={isAutomaticSelection}
            data-test-id="worksheet-create-with-ai-question-count-input"
          />
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            size="small"
            icon={<AddOutlined />}
            onClick={() =>
              updateQuestionCount(isAutomaticSelection ? 1 : questionCount + 1)
            }
            disabled={questionCount >= 10}
            testId="worksheet-create-with-ai-question-count-increment"
          />
        </div>
      </div>
    </div>
  );
};

export default AiQuestionGeneratorQuestionDetails;
